// Screen 1 — Landing (stranger, no account). Three equal calendar choices —
// Google, Microsoft, Apple. The mechanism differs (OAuth vs iCloud email + CalDAV)
// but the UI is uniform; users pick their calendar, not a protocol. Copy discipline:
// never claim "live"/"real-time" — sync is a cache.
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { auth } from '../lib/supabase';
import { capture } from '../lib/analytics';
import { Screen, Card, Eyebrow, Title, PrivacyLine, Button, Spinner } from '../components/ui';
import EmailSignIn from '../components/EmailSignIn';

export default function Landing() {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const [showEmail, setShowEmail] = useState(false);

  // Already signed in → let the first-run chain / Home take over.
  useEffect(() => {
    if (!loading && user) navigate('/auth/callback', { replace: true });
  }, [loading, user, navigate]);

  const signIn = async (provider) => {
    capture('signin_started', { provider });
    await auth.signInWith(provider, { redirectTo: `${window.location.origin}/auth/callback` });
  };

  if (loading) return <Screen><Spinner /></Screen>;

  return (
    <Screen>
      <Card>
        <Eyebrow>when.vennio.app</Eyebrow>
        <Title className="mt-4">See when you're both free.</Title>
        <p className="mt-4 font-sans text-base text-ink/70">
          Share a link, connect calendars, and see the blank spaces you have in
          common. Pick one, and it lands on both calendars.
        </p>

        {/* Privacy line ABOVE the button: sign-in now includes the calendar grant,
            so the promise is framed before the ask is made (R3). */}
        <div className="mt-8">
          <PrivacyLine />

          {showEmail ? (
            <div className="mt-4">
              <button
                onClick={() => setShowEmail(false)}
                className="mb-4 font-mono text-xs text-ink/40 hover:text-ink"
              >
                ← back
              </button>
              <EmailSignIn redirectTo={`${window.location.origin}/auth/callback`} />
            </div>
          ) : (
            <>
              <p className="mt-4 mb-3 font-sans text-base text-ink/70">Who's your calendar with?</p>
              <div className="space-y-3">
                <Button variant="secondary" onClick={() => signIn('google')}>Google</Button>
                <Button variant="secondary" onClick={() => signIn('microsoft')}>Microsoft</Button>
                <Button variant="secondary" onClick={() => setShowEmail(true)}>Apple</Button>
              </div>
            </>
          )}
        </div>

        <p className="mt-6 font-mono text-xs text-ink/50">Updated every 15 minutes.</p>
      </Card>
    </Screen>
  );
}
